/**
 * The CROSSFALL annotation on the section drawing (crossfall spec §6): per
 * band, the two fitted lines drawn faintly and one label above the band's
 * midpoint reading "✓ −2.6 % (design −2.5 %)" in the verdict's ink.
 *
 * Its own component, and its own file, for one reason: `StationSectionChart`
 * is a 2 200-line file under concurrent edit, and everything here has to
 * reach it through a SINGLE insertion. Nothing in this module reads the
 * chart's state — the caller hands it the station line's block and the two
 * scales, and it draws.
 *
 * It computes NO geometry of its own. `asbuiltLine` / `designLine` are the
 * fit's endpoints as `scripts/crossfall.py` produced them, which is the same
 * contract the PDF's `buildStationSectionSvg` consumes — the report twin rule
 * (CLAUDE.md) forbids the arithmetic existing on one side only, so it exists
 * on neither. The only sums here are the two midpoints the label is placed
 * at, which are a drawing decision and not a measurement.
 *
 * Its twin is `processReport/stationSectionSvg.ts`'s `crossfallMarks` block
 * plus the `crossfallSvgOption` caller that feeds it: same sign convention
 * (`left` mirrors onto negative offsets), same dash for the design line, same
 * halo, same `hasFit` guard, and a verdict glyph leading the label. The glyph
 * is not decoration — the section prints, and a verdict carried by colour
 * alone is gone in greyscale and for a red-green colour deficiency.
 *
 * The glyph comes from the CLIENT's `SEVERITY` map, not the report's
 * `VERDICT_GLYPH`, so this label, the table's cell and the header chip agree
 * with each other. The two maps disagree on one row (`MARGINAL`: `~` here,
 * `!` in the PDF) — a pre-existing defect in the two "single source of truth"
 * maps, not something to paper over on this side alone.
 */
import { useTranslation } from 'react-i18next';

import { SEVERITY } from '@/components/VerdictChip';
import { formatSigned } from '@/lib/stationVolumes/format';
import type { CrossfallSide, StationCrossfall } from '@/lib/stationVolumes/types';

/**
 * The ink each verdict's label takes. Not the cut/fill pair: a crossfall
 * verdict is a judgement, not a polarity — a band that falls the wrong way is
 * not "cut". `--sv-haul` is this dock's neutral, which is what a PASS wants:
 * the drawing should not shout about the bands that are fine.
 */
const VERDICT_INK: Record<string, string> = {
  PASS: 'var(--sv-haul)',
  MARGINAL: 'var(--sv-caution)',
  FAIL: 'var(--sv-cut)',
  INCONCLUSIVE: 'var(--sv-clear)',
};

/**
 * The same guard as the PDF's `crossfallHasFit`, and for the same reason: a
 * side whose figures the worker could not state must not have them printed.
 * All three are checked, not just the two this label prints: `deltaPct` is
 * what the side was JUDGED on, so a side missing it was never judged, and a
 * guard that let it through would print a slope pair under a verdict nothing
 * computed. Keep this expression character-for-character in step with the
 * PDF's — two near-identical guards drifting apart is how the data band ends
 * up saying "no fit" under a label reading "−2.6 % (design −2.5 %)".
 */
const hasFit = (b: CrossfallSide): boolean => b.asbuiltPct != null && b.designPct != null && b.deltaPct != null;

export interface SectionCrossfallLabelsProps {
  crossfall: StationCrossfall;
  /** Signed OFFSET (m) -> px. */
  xScale: (offsetM: number) => number;
  /** Elevation (m) -> px. */
  yScale: (z: number) => number;
  /** Plot height, so a label for an unresolved band still lands on the pane. */
  plotH: number;
}

export function SectionCrossfallLabels({ crossfall, xScale, yScale, plotH }: SectionCrossfallLabelsProps) {
  const { t } = useTranslation('project');
  const sides: ['left' | 'right', CrossfallSide | null][] = [
    ['left', crossfall.left ?? null],
    ['right', crossfall.right ?? null],
  ];
  if (!sides.some(([, b]) => b)) return null;

  return (
    <g data-testid='sv-section-crossfall' pointerEvents='none'>
      {sides.map(([side, b]) => {
        if (!b) return null;
        // `u` is the UNSIGNED offset the fit was made against; the left band
        // lives at NEGATIVE offsets on the drawing.
        const sign = side === 'left' ? -1 : 1;
        // Both lookups are defended the same way. A verdict this build does
        // not know draws as unresolved; one of them throwing while the other
        // fell back would take the whole section pane down for a row neither
        // map has ever been missing.
        const ink = VERDICT_INK[b.verdict] ?? VERDICT_INK.INCONCLUSIVE;
        const glyph = (SEVERITY[b.verdict] ?? SEVERITY.INCONCLUSIVE).glyph;
        const seg = (fit: NonNullable<CrossfallSide['asbuiltLine']>, role: 'asbuilt' | 'design') => (
          <line
            key={role}
            data-testid={`sv-section-xf-line-${role}-${side}`}
            x1={xScale(sign * fit.u0)}
            y1={yScale(fit.z0)}
            x2={xScale(sign * fit.u1)}
            y2={yScale(fit.z1)}
            stroke={ink}
            strokeOpacity={role === 'design' ? 0.5 : 0.8}
            strokeWidth={1.25}
            strokeDasharray={role === 'design' ? '2,3' : undefined}
            strokeLinecap='butt'
          />
        );
        // The label sits above the band's midpoint, on the as-built fit when
        // there is one and at the middle of the pane when there is not.
        const anchorFit = b.asbuiltLine ?? b.designLine;
        const xMid = anchorFit ? xScale((sign * (anchorFit.u0 + anchorFit.u1)) / 2) : xScale(sign * 2);
        const yMid = anchorFit ? yScale((anchorFit.z0 + anchorFit.z1) / 2) : plotH / 2;
        // An INCONCLUSIVE side says WHY in words. `reason: null` is a side the
        // worker could not fit and did not say why — naming a cause there
        // would state as fact something the data never claimed, so it falls
        // back to the same "could not resolve" sentence the table's cell uses,
        // never to one of the two named reasons.
        const figures = hasFit(b)
          ? t('stationVolumes.section.crossfallLabel', {
              asbuilt: formatSigned(b.asbuiltPct!, 1),
              design: formatSigned(b.designPct!, 1),
            })
          : b.reason
            ? t(`stationVolumes.table.crossfallReason.${b.reason}`)
            : t('stationVolumes.table.crossfallNa');
        return (
          <g key={side}>
            {b.designLine ? seg(b.designLine, 'design') : null}
            {b.asbuiltLine ? seg(b.asbuiltLine, 'asbuilt') : null}
            <text
              data-testid={`sv-section-xf-label-${side}`}
              data-verdict={b.verdict}
              x={xMid}
              y={Math.max(10, yMid - 8)}
              fontSize={10}
              fontWeight={600}
              fill={ink}
              textAnchor='middle'
              // A halo, the same trick the PDF's callouts use, so the figure
              // stays readable over the surfaces it annotates.
              paintOrder='stroke'
              stroke='var(--sv-surface)'
              strokeWidth={3}
              strokeLinejoin='round'
            >
              {`${glyph} ${figures}`}
              {/* The glyph is a mark, not a word. The verdict spelled out is
                  what a screen reader and a hover need. */}
              <title>{t(`expectation.verdict.${b.verdict}`)}</title>
            </text>
          </g>
        );
      })}
    </g>
  );
}
